// What an admin should check before approving a host invoice. The numbers are
// already consistent (the database enforces that); these are the things only a
// person can judge, laid out so none is missed.

export interface ReviewInvoice {
  host_id: string;
  status: string;
  currency: string;
  total_cents: number;
  invoice_base_cents: number;
  invoice_vat_cents: number;
  invoice_vat_exemption: string | null;
  invoice_withholding_cents: number;
  invoice_payable_cents: number;
  invoice_date: string;
  submitted_at: string;
}

export interface ReviewItem {
  amount_cents: number;
  released_at: string | null;
}

export interface ReviewBilling {
  legal_name: string;
  nif: string;
  iban: string;
  iban_changed_at: string;
}

export interface ReviewCheck {
  tone: 'ok' | 'warn' | 'bad';
  text: string;
}

const DAY = 86_400_000;

function money(cents: number, currency: string): string {
  return new Intl.NumberFormat('en', { style: 'currency', currency: currency.toUpperCase() }).format(cents / 100);
}

export function reviewChecks(inv: ReviewInvoice, items: ReviewItem[], billing: ReviewBilling | null, adminId: string, now: Date): ReviewCheck[] {
  const checks: ReviewCheck[] = [];
  const live = items.filter((i) => i.released_at === null);
  const itemsTotal = live.reduce((n, i) => n + i.amount_cents, 0);

  checks.push(itemsTotal === inv.total_cents
    ? { tone: 'ok', text: `The ${live.length} trip${live.length === 1 ? '' : 's'} add up to ${money(inv.total_cents, inv.currency)}, the invoice total.` }
    : { tone: 'bad', text: `The trips add up to ${money(itemsTotal, inv.currency)} but the invoice total is ${money(inv.total_cents, inv.currency)}. Reject it.` });

  checks.push(inv.invoice_vat_cents > 0
    ? { tone: 'ok', text: `The host charges VAT: ${money(inv.invoice_vat_cents, inv.currency)} on a base of ${money(inv.invoice_base_cents, inv.currency)}. Check it on the recibo.` }
    : { tone: 'ok', text: `No VAT: “${inv.invoice_vat_exemption ?? ''}”. Check that the recibo says the same.` });

  if (inv.invoice_withholding_cents > 0) {
    checks.push({
      tone: 'warn',
      text: `Tax of ${money(inv.invoice_withholding_cents, inv.currency)} is withheld at source. We pay ${money(inv.invoice_payable_cents, inv.currency)}, and the withheld tax is owed to the tax authority.`,
    });
  }

  if (!billing) {
    checks.push({ tone: 'bad', text: 'The host has no billing details, so there is no account to pay.' });
  } else {
    checks.push({ tone: 'ok', text: `Pay ${billing.legal_name}, NIF ${billing.nif}. The recibo must show the same name and NIF.` });
    const changed = new Date(billing.iban_changed_at).getTime();
    if (changed > new Date(inv.submitted_at).getTime()) {
      checks.push({ tone: 'warn', text: 'The host changed their IBAN after sending this invoice. Confirm the new account with them before approving.' });
    } else if (now.getTime() - changed < 30 * DAY) {
      const days = Math.max(0, Math.floor((now.getTime() - changed) / DAY));
      checks.push({ tone: 'warn', text: `The IBAN was added or changed ${days === 0 ? 'today' : `${days} day${days === 1 ? '' : 's'} ago`}. Consider confirming it with the host.` });
    }
  }

  if (inv.host_id === adminId) {
    checks.push({ tone: 'bad', text: 'This is your own invoice, so another admin has to approve it.' });
  }
  return checks;
}
